import { FC, useMemo } from "react";
import { ScrollView } from "react-native";
import {
  Bar,
  CartesianGrid,
  Legend,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Box, Center, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";

interface DataPoint {
  x: string;
  y: number;
}

interface Group {
  key: string;
  label: string;
  points: DataPoint[];
  respondentCount: number;
}

interface GroupedBarChartProps {
  compact?: boolean;
  groups: Group[];
  minWidth?: number;
}

const TICK_COLOR = "hsl(220,10%,46%)";
const GRID_STROKE = "hsl(220,14%,93%)";
const CHARS_PER_LINE = 10;
const LINE_HEIGHT = 13;
const NUMBERED_RE = /^\d+[.)]\s+/;
const BAR_AREA_HEIGHT = 280;

const stripNumber = (s: string) => s.replace(NUMBERED_RE, "");

function wrapWords(text: string): string[] {
  const words = stripNumber(text).split(" ");
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= CHARS_PER_LINE) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current =
        word.length > CHARS_PER_LINE
          ? `${word.substring(0, CHARS_PER_LINE)}…`
          : word;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function WrappedXTick({
  x,
  y,
  payload,
}: {
  payload?: { value: string };
  x?: number;
  y?: number;
}) {
  const lines = wrapWords(payload?.value ?? "");
  return (
    <text
      fill={TICK_COLOR}
      fontSize={11}
      textAnchor="middle"
      x={x}
      y={(y ?? 0) + 8}
    >
      {lines.map((line, i) => (
        <tspan dy={i === 0 ? 0 : LINE_HEIGHT} key={i} x={x}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

// Same categorical feel as Price Elasticity — brand purple first, then clear hues
const GROUPED_BAR_PALETTE = [
  "#8b5cf6", // brand purple (primary-500)
  "hsl(217, 91%, 60%)", // blue
  "hsl(38, 92%, 50%)", // amber
  "hsl(160, 84%, 39%)", // teal green
  "hsl(0, 75%, 60%)", // coral red
  "hsl(190, 70%, 45%)", // cyan
  "hsl(340, 65%, 58%)", // rose
  "hsl(25, 80%, 55%)", // orange
  "hsl(90, 50%, 45%)", // olive
  "hsl(200, 55%, 50%)", // steel blue
  "hsl(150, 45%, 45%)", // sage
];

function GroupedTooltip({
  active,
  payload,
  label,
  formatPercent,
  groups,
  palette,
}: {
  active?: boolean;
  formatPercent: (v: number) => string;
  groups: Group[];
  label?: string;
  palette: string[];
  payload?: {
    dataKey: string;
    name: string;
    payload: Record<string, number>;
    value: number;
  }[];
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: "0 0 4px",
        }}
      >
        {label}
      </p>
      {payload.map((item, idx) => {
        const groupIdx = groups.findIndex((g) => g.key === item.dataKey);
        const color =
          palette[(groupIdx >= 0 ? groupIdx : idx) % palette.length];
        const count = item.payload[`${item.dataKey}_count`];
        return (
          <p
            key={item.dataKey}
            style={{ color, fontSize: 12, margin: "1px 0" }}
          >
            {item.name}: {formatPercent(Math.round(item.value))} · N={count}
          </p>
        );
      })}
    </div>
  );
}

const GroupedBarChart: FC<GroupedBarChartProps> = ({
  compact = false,
  groups,
  minWidth = 300,
}) => {
  const { i18n } = useLocalization();

  const palette = GROUPED_BAR_PALETTE;

  const { barWidth, bottomMargin, chartData, chartHeight, chartWidth } =
    useMemo(() => {
      const allXLabels = new Set<string>();
      groups.forEach((group) =>
        group.points.forEach((p) => allXLabels.add(p.x))
      );

      const labels = Array.from(allXLabels);
      const optionCount = labels.length;
      const groupCount = groups.length;

      const maxLines = Math.max(...labels.map((l) => wrapWords(l).length));
      const bottom = maxLines * LINE_HEIGHT + 14;

      const calculatedBarWidth = Math.max(
        28 - groupCount * 1.0 - optionCount * 0.3,
        10
      );
      const clusterWidth =
        calculatedBarWidth * groupCount + 2 * (groupCount - 1);
      const clusterGap = Math.min(Math.max(clusterWidth * 0.18, 12), 40);
      const spacePerOption = clusterWidth + clusterGap;
      const extraPadding = Math.max(0, 100 - groupCount * 5 - optionCount * 10);
      const calculatedWidth = Math.max(
        150 + spacePerOption * optionCount + extraPadding * 2,
        280
      );

      const data = labels.map((label) => {
        const entry: Record<string, string | number> = { name: label };
        groups.forEach((group) => {
          const point = group.points.find((p) => p.x === label);
          const raw = point?.y ?? 0;
          entry[group.key] =
            group.respondentCount > 0 ? (raw / group.respondentCount) * 100 : 0;
          entry[`${group.key}_count`] = raw;
        });
        return entry;
      });

      return {
        barWidth: calculatedBarWidth,
        bottomMargin: bottom,
        chartData: data,
        chartHeight: BAR_AREA_HEIGHT + 20 + bottom,
        chartWidth: calculatedWidth,
      };
    }, [groups]);

  if (groups.length === 0 || groups[0]?.points.length === 0) {
    return (
      <Center className="h-[450px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const formatPercent = (value: number) =>
    i18n.numberToPercentage(value, { precision: 0 });

  const chartMargin = {
    bottom: bottomMargin,
    left: 20,
    right: 20,
    top: 20,
  };

  const sharedChildren = (
    <>
      <CartesianGrid
        stroke={GRID_STROKE}
        strokeDasharray="3 3"
        vertical={false}
      />
      <XAxis
        dataKey="name"
        interval={0}
        tick={<WrappedXTick />}
        tickLine={false}
      />
      <YAxis
        axisLine={false}
        domain={[0, 100]}
        tick={{ fill: TICK_COLOR, fontSize: 11 }}
        tickCount={5}
        tickFormatter={(v) => formatPercent(Math.round(v))}
        tickLine={false}
      />
      <Tooltip
        content={
          <GroupedTooltip
            formatPercent={formatPercent}
            groups={groups}
            palette={palette}
          />
        }
        cursor={{ fill: "hsl(220,14%,97%)" }}
        itemSorter={undefined}
      />
      <Legend
        iconSize={8}
        iconType="circle"
        itemSorter={null}
        wrapperStyle={{
          animationDuration: "0ms",
          fontFamily: "inherit",
          fontSize: 11,
          paddingTop: 12,
          transition: "none",
        }}
      />
      {groups.map((group, idx) => (
        <Bar
          animationBegin={0}
          animationDuration={600}
          animationEasing="ease-out"
          barSize={barWidth}
          dataKey={group.key}
          fill={palette[idx % palette.length]}
          isAnimationActive={true}
          key={group.key}
          name={group.label}
          radius={[4, 4, 0, 0]}
        />
      ))}
    </>
  );

  const needsScroll = chartWidth > 650;

  return (
    <Box className="w-full">
      {needsScroll ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <RechartsBarChart
            data={chartData}
            height={chartHeight}
            margin={chartMargin}
            width={chartWidth}
          >
            {sharedChildren}
          </RechartsBarChart>
        </ScrollView>
      ) : (
        <div style={{ maxWidth: chartWidth, width: "100%" }}>
          <ResponsiveContainer height={chartHeight} width="100%">
            <RechartsBarChart data={chartData} margin={chartMargin}>
              {sharedChildren}
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Box>
  );
};

export default GroupedBarChart;
